// Calendar utility for generating .ics files
// Works with Google Calendar, Apple Calendar, Outlook, etc.
import { Capacitor } from '@capacitor/core'
import { API_BASE } from '../services/api'
import { wallTimeToInstant } from './dateUtils.js'

/**
 * ★★★ v242.106.0 — THE .ics IS NOW A URL, AND THERE IS NO CLIENT SERIALISER.
 *
 * TF b39 #6: "Save ics file is a download but cant we make easier to Add."
 * The file was correct; the DESTINATION was wrong. `saveOrShareBlob` wrote it
 * into the cache dir and opened `Share.share()`, so iOS presented the SHARE
 * sheet — AirDrop, Messages, Save to Files — and adding it to Calendar took
 * four more taps. That share path was itself a fix (the previous `<a download>`
 * was a silent no-op in WKWebView, TF b34 #16), so this is the third route:
 * hand iOS the file's URL and let iOS present its own "Add Event" sheet.
 *
 * ★★ SFSafariViewController TAKES A URL AND NOTHING ELSE, which is why
 *    `generateICS()` is DELETED rather than kept for the web. Two serialisers
 *    for one format is how the web download and the phone's calendar entry come
 *    to disagree about the alarm offsets — and only one of them would ever be
 *    the one anybody tested. The single serialiser is the API's
 *    `_appointment_ics()` (v243.107.0), gated by tools/test_appointment_ics.py.
 *
 * ★ Web keeps behaving like a download because the SERVER says `inline` and the
 *   browser decides; nothing here branches on platform except HOW the URL is
 *   opened — @capacitor/browser presents it OVER the app so the SPA is never
 *   navigated away (walletPass.js explains what that cost last time).
 */
export function appointmentIcsUrl(appointmentId, lang) {
  if (!appointmentId) return null
  const q = lang ? `?lang=${encodeURIComponent(lang)}` : ''
  return `${API_BASE}/appointments/${appointmentId}/calendar.ics${q}`
}

export async function openAppointmentIcs(appointmentId, lang) {
  const url = appointmentIcsUrl(appointmentId, lang)
  if (!url) throw new Error('This visit has no appointment record to add.')
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import('@capacitor/browser')
    await Browser.open({ url, presentationStyle: 'popover' })
    return { method: 'browser' }
  }
  window.open(url, '_blank', 'noopener')
  return { method: 'window' }
}

/**
 * ★ PROD-1368: a visit's `date` + `time` are the practice's wall clock (ORELLA_TZ). The exported event
 * is the true instant from dateUtils.wallTimeToInstant (no time -> 9:00 AM practice time). An unusable
 * date gives null and the link opens without a time rather than at a wrong one.
 */
const visitSpan = (date, time, duration) => {
  const start = wallTimeToInstant(String(date || '').slice(0, 10), time || '09:00')
  if (!start) return null
  const minutes = Number(duration) > 0 ? Number(duration) : 30
  return { start, end: new Date(start.getTime() + minutes * 60 * 1000) }
}
/** Google's UTC form: YYYYMMDDTHHmmssZ. */
const googleUtc = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

/**
 * Generate Google Calendar URL for adding an event
 * @param {Object} appointment - Appointment details
 * @returns {string} - Google Calendar add event URL
 */
export function getGoogleCalendarUrl(appointment) {
  const {
    title = 'Orella Appointment',
    description = '',
    date,
    time,
    duration = 30,
    location = '',
  } = appointment

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    details: description,
    location: location,
  })
  const span = visitSpan(date, time, duration)
  // ★ PROD-1368: UTC with a trailing Z is an instant. The old floating `YYYYMMDDTHHmmss` (built with
  //   setHours in the DEVICE zone) was read in the viewer's Google calendar zone, so a 1:00 PM LA
  //   visit landed at 1:00 PM New York time.
  if (span) params.set('dates', `${googleUtc(span.start)}/${googleUtc(span.end)}`)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/**
 * Generate Outlook Calendar URL for adding an event
 * @param {Object} appointment - Appointment details
 * @returns {string} - Outlook Calendar add event URL
 */
export function getOutlookCalendarUrl(appointment) {
  const {
    title = 'Orella Appointment',
    description = '',
    date,
    time,
    duration = 30,
    location = '',
  } = appointment

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    body: description,
    location: location,
  })
  const span = visitSpan(date, time, duration)
  // ★ PROD-1368: the instant from the ORELLA_TZ wall time, not setHours in the device zone.
  if (span) { params.set('startdt', span.start.toISOString()); params.set('enddt', span.end.toISOString()) }
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`
}

export default {
  appointmentIcsUrl,
  openAppointmentIcs,
  getGoogleCalendarUrl,
  getOutlookCalendarUrl,
}
